import React, { useEffect } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { ArrowLeft } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import type { PayoutAccountInfo, PayoutMethod } from './PayoutDialog.types';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { calcFeeInMinorUnits } from './payoutFeeUtils';
import { useMount } from '@shared/utils/hooks/useMount';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { pxToRem } from '@withjoy/joykit/theme';

type MethodStepProps = Readonly<{
  payoutAccountInfo: PayoutAccountInfo;
  amountInMinorUnits: number;
  selectedMethod: PayoutMethod;
  onMethodChange: (method: PayoutMethod) => void;
  onTransfer: () => void;
  loadingTransfer: boolean;
  onBack: () => void;
  onPageViewed?: () => void;
}>;

const RadioCheck: React.FC = () => (
  <svg
    width="10"
    height="8"
    viewBox="0 0 10 8"
    fill="none"
    stroke="white"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="1 4 3.5 6.5 9 1" />
  </svg>
);

export const MethodStep: React.FC<MethodStepProps> = ({
  payoutAccountInfo,
  amountInMinorUnits,
  selectedMethod,
  onMethodChange,
  onTransfer,
  loadingTransfer,
  onBack,
  onPageViewed,
}) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatMinorUnitsCurrency, formatCurrency } = useCurrencyFormatter();

  useMount(() => {
    onPageViewed?.();
  });

  const currencyCode = payoutAccountInfo.balance?.available.currencyCode ?? payoutAccountInfo.currencyCode ?? DEFAULT_CURRENCY;
  const externalAccount = payoutAccountInfo.defaultExternalAccount;
  const institutionDisplay = externalAccount ? `${externalAccount.institutionName ? `${externalAccount.institutionName} ` : ''}••••${externalAccount.last4}` : null;

  const availablePayoutMethods = externalAccount?.availablePayoutMethods ?? ['standard'];
  const [isInstantPayoutFlagEnabled] = useIsFeatureEnabled('registryInstantPayoutEnabled');

  const instantMinFeeInMinorUnits = payoutAccountInfo.instantPayoutFee?.minFeeInMinorUnits;
  const isInstantPayoutEligible =
    availablePayoutMethods.includes('instant') &&
    (instantMinFeeInMinorUnits == null || amountInMinorUnits > instantMinFeeInMinorUnits) &&
    payoutAccountInfo.instantPayoutFee !== null;
  const isInstantEnabled = isInstantPayoutEligible && isInstantPayoutFlagEnabled;

  useEffect(() => {
    if (!isInstantEnabled && selectedMethod === 'instant') {
      onMethodChange('standard');
    }
  }, [isInstantEnabled, selectedMethod, onMethodChange]);

  const selectedFee = selectedMethod === 'instant' ? payoutAccountInfo.instantPayoutFee : payoutAccountInfo.standardPayoutFee;
  const feeInMinorUnits = selectedFee ? calcFeeInMinorUnits(amountInMinorUnits, selectedFee) : 0;
  const netAmountInMinorUnits = Math.max(0, amountInMinorUnits - feeInMinorUnits);
  const formattedNetAmount = formatMinorUnitsCurrency(netAmountInMinorUnits, currencyCode);
  const formattedAmount = formatMinorUnitsCurrency(amountInMinorUnits, currencyCode);
  const formatFeeLabel = (method: PayoutMethod): string => {
    const fee = method === 'instant' ? payoutAccountInfo.instantPayoutFee : payoutAccountInfo.standardPayoutFee;
    if (!fee || (fee.rate === 0 && fee.fixedValueInMinorUnits === 0)) {
      return tq`Free`;
    }
    const pct = fee.rate > 0 ? `${(fee.rate * 100).toFixed(2).replace(/\.?0+$/, '')}%` : null;
    const flat =
      fee.fixedValueInMinorUnits > 0
        ? formatCurrency({
            priceFloatingPointDecimalString: String(minorToMajorUnits(fee.fixedValueInMinorUnits, currencyCode)),
            priceCurrencyCode: currencyCode,
            formatForm: 'auto',
          })
        : null;
    const feeDesc = pct && flat ? `${pct} + ${flat}` : (pct ?? flat ?? '');
    if (fee.minFeeInMinorUnits != null && fee.minFeeInMinorUnits > 0) {
      const minFeeFormatted = formatCurrency({
        priceFloatingPointDecimalString: String(minorToMajorUnits(fee.minFeeInMinorUnits, currencyCode)),
        priceCurrencyCode: currencyCode,
        formatForm: 'auto',
      });
      return tq`${feeDesc} transfer fee · min. ${minFeeFormatted}`;
    }
    return tq`${feeDesc} transfer fee`;
  };

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      {/* Header: back + title */}
      <Flex
        alignItems="center"
        marginBottom={6}
        position="relative"
      >
        <ButtonV2
          variant="ghost"
          intent="neutral"
          size="sm"
          onClick={onBack}
          paddingX={0}
          position="absolute"
          left={0}
        >
          <ArrowLeft />
        </ButtonV2>
        <TextV2
          flex={1}
          textAlign="center"
          fontSize={pxToRem(24)}
          fontWeight={600}
          color="mono14"
          letterSpacing="-0.024em"
          lineHeight="1.3"
        >
          {tq`Transfer to bank`}
        </TextV2>
      </Flex>

      <Flex
        flexDirection="column"
        flex={1}
        justifyContent="center"
      >
        {/* Transfer amount */}
        <TextV2
          textAlign="center"
          fontSize={pxToRem(36)}
          fontWeight={600}
          color="mono14"
          letterSpacing="-0.54px"
          lineHeight="1.2"
          marginBottom={6}
        >
          {formattedAmount}
        </TextV2>

        {/* Question */}
        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          fontWeight={600}
          color="mono14"
          lineHeight="1.4"
          marginBottom={4}
        >
          {tq`How soon do you need it?`}
        </TextV2>

        {/* Option cards */}
        <Flex
          flexDirection="column"
          gap={2}
          marginBottom={4}
        >
          <Flex
            borderStyle="solid"
            borderWidth={isInstantEnabled && selectedMethod === 'instant' ? '2px' : '1px'}
            borderColor={isInstantEnabled && selectedMethod === 'instant' ? 'mono14' : 'mono3'}
            borderRadius={3}
            paddingX={4}
            paddingY={pxToRem(18)}
            alignItems="center"
            gap={3}
            cursor={isInstantEnabled ? 'pointer' : 'default'}
            backgroundColor={isInstantEnabled ? 'white' : 'mono1'}
            onClick={isInstantEnabled ? () => onMethodChange('instant') : undefined}
          >
            <Box flex={1}>
              {isInstantEnabled ? (
                <Box marginBottom={1}>
                  <Box
                    as="span"
                    fontSize={pxToRem(15)}
                    fontWeight={600}
                    color="mono14"
                  >
                    {tq`Instant`}
                  </Box>
                  <Box
                    as="span"
                    fontSize={pxToRem(15)}
                    fontWeight={700}
                    color="mono14"
                  >
                    {' · '}
                    {tq`within 24 hours`}
                  </Box>
                </Box>
              ) : (
                <Flex
                  alignItems="center"
                  gap={2}
                  marginBottom={1}
                >
                  <Box
                    as="span"
                    fontSize={pxToRem(15)}
                    fontWeight={600}
                    color="mono6"
                  >
                    {tq`Instant`}
                  </Box>
                  <Box
                    as="span"
                    display="inline-block"
                    fontSize={pxToRem(10)}
                    fontWeight={700}
                    letterSpacing="0.04em"
                    textTransform="uppercase"
                    color="mono8"
                    backgroundColor="mono3"
                    borderRadius="9999px"
                    paddingX={2}
                    paddingY={pxToRem(3)}
                  >
                    {tq`Unavailable`}
                  </Box>
                </Flex>
              )}
              {institutionDisplay && (
                <TextV2
                  fontSize={pxToRem(13)}
                  color={isInstantEnabled ? 'mono10' : 'mono6'}
                  lineHeight="1.5"
                  letterSpacing="-0.032px"
                >
                  {institutionDisplay}
                </TextV2>
              )}
              <TextV2
                fontSize={pxToRem(13)}
                color={isInstantEnabled ? 'mono10' : 'mono8'}
                lineHeight="1.5"
                marginTop={1}
              >
                {isInstantEnabled ? formatFeeLabel('instant') : tq`Not available for this payment method.`}
              </TextV2>
            </Box>
            <Box
              width={pxToRem(18)}
              height={pxToRem(18)}
              borderRadius="50%"
              borderStyle="solid"
              borderWidth="1.5px"
              borderColor={isInstantEnabled ? (isInstantEnabled && selectedMethod === 'instant' ? 'mono14' : 'mono5') : 'mono4'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              backgroundColor={isInstantEnabled && selectedMethod === 'instant' ? 'mono14' : 'transparent'}
              flexShrink={0}
            >
              {isInstantEnabled && selectedMethod === 'instant' && <RadioCheck />}
            </Box>
          </Flex>

          {/* Standard */}
          <Flex
            borderStyle="solid"
            borderWidth={selectedMethod === 'standard' ? '2px' : '1px'}
            borderColor={selectedMethod === 'standard' ? 'mono14' : 'mono3'}
            borderRadius={3}
            paddingX={4}
            paddingY={pxToRem(18)}
            alignItems="center"
            gap={3}
            cursor="pointer"
            backgroundColor="white"
            onClick={() => onMethodChange('standard')}
          >
            <Box flex={1}>
              <TextV2
                fontSize={pxToRem(15)}
                fontWeight={600}
                color="mono14"
                lineHeight="1.4"
                marginBottom={1}
              >
                {tq`5–7 business days`}
              </TextV2>
              {institutionDisplay && (
                <TextV2
                  fontSize={pxToRem(13)}
                  color="mono10"
                  lineHeight="1.5"
                  letterSpacing="-0.032px"
                >
                  {institutionDisplay}
                </TextV2>
              )}
              <TextV2
                fontSize={pxToRem(13)}
                color="mono10"
                lineHeight="1.5"
                marginTop={1}
              >
                {formatFeeLabel('standard')}
              </TextV2>
            </Box>
            <Box
              width={pxToRem(18)}
              height={pxToRem(18)}
              borderRadius="50%"
              borderStyle="solid"
              borderWidth={selectedMethod === 'standard' ? '2px' : '1.5px'}
              borderColor={selectedMethod === 'standard' ? 'mono14' : 'mono5'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              backgroundColor={selectedMethod === 'standard' ? 'mono14' : 'transparent'}
              flexShrink={0}
            >
              {selectedMethod === 'standard' && <RadioCheck />}
            </Box>
          </Flex>
        </Flex>

        {/* Disclaimer */}
        <TextV2
          fontSize={pxToRem(10)}
          color="mono8"
          lineHeight="1.5"
          marginBottom={4}
        >
          {tq`Account security checks may require additional time to make funds available.`}
          {isInstantEnabled &&
            (() => {
              const fee = payoutAccountInfo.instantPayoutFee;
              const pct = fee && fee.rate > 0 ? `${(fee.rate * 100).toFixed(2).replace(/\.?0+$/, '')}%` : null;
              const minFee = fee?.minFeeInMinorUnits != null && fee.minFeeInMinorUnits > 0 ? formatMinorUnitsCurrency(fee.minFeeInMinorUnits, currencyCode) : null;
              const maxAmount = payoutAccountInfo.payoutLimitInMinorUnits != null ? formatMinorUnitsCurrency(payoutAccountInfo.payoutLimitInMinorUnits, currencyCode) : null;
              return (
                <>
                  {' '}
                  {tq`Instant typically posts immediately but can take up to 24 hours.`}
                  {pct && (
                    <>
                      {' '}
                      {minFee && maxAmount
                        ? tq`Instant transfer fee is ${pct} with a minimum ${minFee} fee and maximum transfer of ${maxAmount}.`
                        : minFee
                          ? tq`Instant transfer fee is ${pct} with a minimum ${minFee} fee.`
                          : maxAmount
                            ? tq`Instant transfer fee is ${pct} and maximum transfer of ${maxAmount}.`
                            : tq`Instant transfer fee is ${pct}.`}
                    </>
                  )}
                </>
              );
            })()}
        </TextV2>
      </Flex>

      <ButtonV2
        onClick={onTransfer}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        marginTop={6}
        loading={loadingTransfer}
      >
        {tq`Transfer ${formattedNetAmount}`}
      </ButtonV2>
    </Flex>
  );
};
